import React, { useCallback, useMemo, useState } from 'react';
import sortBy from 'lodash/sortBy';
import { TransitionGroup } from 'react-transition-group';
import { Collapse, List, styled, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useCreateTicketCrumb, useSelectedComment } from '@tymely/services';
import { IArgument, IArgumentUpdate, IDineshTicketOperations, requiresInput } from '@tymely/atoms';
import { yellow } from '@mui/material/colors';
import {
    useApproveQaMutation,
    useQaRejectMutation,
    useQaRejectRevertMutation,
} from '@tymely/features/Ticket/QaMode/services';
import { useQaStepperContext } from '@tymely/features/Ticket/QaMode/QaStepperContext';
import { getArgumentFindFunction } from '@tymely/utils/arguments';
import QaArgumentListItem from '@tymely/features/Ticket/QaMode/QaArguments/QaArgumentListItem';
import { getDisplayRegulatorArg, getGroupArg, getSearchArg } from '@tymely/features/Ticket/Arguments/Sections';
import { STEP_RESOLUTION } from '@tymely/features/Ticket/QaMode/constants';

const _List = styled(List)`
    padding: 0;
`;

const _Collapse = styled(Collapse)`
    background: ${yellow[200]};
    &.MuiCollapse-entered {
        background: 0;
    }
    &.MuiCollapse-hidden {
        background: 0;
    }
`;

const QaArgumentList = () => {
    const [touchedArgIds, setTouchedArgs] = useState<Set<IArgument['md_id']>>(new Set());

    const onTouch = useCallback(
        (arg: IArgument) => setTouchedArgs((ids) => new Set([...ids, arg.md_id])),
        [setTouchedArgs],
    );

    const [lastUpdatedArgs, setLastUpdatedArgs] = useState<IArgument[]>([]);
    const {
        textArgs,
        approvedArgs,
        refetchArgs,
        originalArgs,
        setApprovedArgs,
        setActiveStep,
        args: allArgs,
    } = useQaStepperContext();
    const qaRejectMutation = useQaRejectMutation();
    const qaApproveArgMutation = useApproveQaMutation();
    const qaRejectRevertMutation = useQaRejectRevertMutation();
    const createTicketCrumb = useCreateTicketCrumb();

    const args = useMemo(() => {
        return sortBy(textArgs, [(arg) => Number(arg.order)]);
    }, [textArgs]);
    const selectedComment = useSelectedComment();

    const firstUnapprovedArgAt = useMemo(() => {
        const unapprovedAt = args.findIndex(
            (arg) => !approvedArgs || !approvedArgs.has(arg.md_id) || requiresInput(arg),
        );
        return unapprovedAt >= 0 ? unapprovedAt : args.length;
    }, [args, approvedArgs]);

    const handleApprove = (argument: IArgument, isRevertRejection = false) => {
        if (isRevertRejection) {
            qaRejectRevertMutation.mutate(argument.id);
        }
        createTicketCrumb(IDineshTicketOperations.QA_USER_APPROVED_ARGUMENT, {
            argument,
        });
        qaApproveArgMutation.mutate(argument.id, {
            onSuccess: () => {
                // same as optimistic update
                refetchArgs();
                setApprovedArgs((prev) => {
                    const newSet = new Set(prev);
                    newSet.add(argument.md_id);
                    if (newSet.size === textArgs?.length) {
                        setActiveStep(STEP_RESOLUTION);
                    }
                    return newSet;
                });
            },
        });
    };
    const _onUpdate = useCallback(
        (updates: IArgumentUpdate[], isApproved = false) => {
            setLastUpdatedArgs(
                updates.map((update) => (textArgs as IArgument[]).find((a) => a.id === update.id)).filter(Boolean),
            );
            if (!isApproved) {
                updates.forEach((update) => {
                    const originalArg = (originalArgs as IArgument[]).find(
                        (arg) => arg.md_id === update.md_id,
                    ) as IArgument;
                    if (
                        originalArg &&
                        originalArg.value === update.value &&
                        originalArg.special_value == update.special_value
                    ) {
                        handleApprove(originalArg, true);
                    } else {
                        const update_data = {
                            id: update.id,
                            value: update.value,
                            special_value: update.special_value,
                        };
                        createTicketCrumb(IDineshTicketOperations.QA_USER_MODIFIED_ARGUMENT, {
                            update_data,
                        });
                        qaRejectMutation.mutate(update_data, {
                            onSuccess: () => {
                                refetchArgs();
                            },
                        });
                    }
                });
            }
        },
        [textArgs, selectedComment, setLastUpdatedArgs, originalArgs],
    );

    const withApproval = !!approvedArgs;

    return (
        <>
            {args.length === 0 && <Typography variant="body2">No items</Typography>}
            {args.length > 0 && !originalArgs && (
                <Typography variant="body2" color="text.secondary">
                    Loading arguments...
                </Typography>
            )}
            {args.length > 0 && originalArgs && (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <_List dense>
                        <TransitionGroup appear={false} exit={false}>
                            {args.map((arg, index) => {
                                const groupArgument = getGroupArg(arg, allArgs as IArgument[]);
                                const searchArgument = getSearchArg(groupArgument ?? arg, allArgs as IArgument[]);
                                const displayRegulatorArgument = getDisplayRegulatorArg(
                                    groupArgument ?? arg,
                                    allArgs as IArgument[],
                                );
                                const approved = approvedArgs?.has(arg.md_id);
                                const rejected = !approved && arg.qa_at && arg.deleted_at;

                                const isLoadingAfterAccessoryArgumentChange = !!lastUpdatedArgs.find(
                                    getArgumentFindFunction(arg),
                                );
                                const isEditable =
                                    !withApproval ||
                                    (approved && index < firstUnapprovedArgAt) ||
                                    index === firstUnapprovedArgAt;
                                const onItemUpdate = (argUpdate: IArgumentUpdate[], isApproved = false) => {
                                    _onUpdate(argUpdate, isApproved);
                                    onTouch?.(arg);
                                };
                                const className = touchedArgIds.has(arg.md_id) ? 'touched' : 'untouched';

                                return (
                                    <_Collapse key={arg.name} className={className} onClick={() => onTouch?.(arg)}>
                                        <QaArgumentListItem
                                            argument={arg}
                                            handleApprove={handleApprove}
                                            groupArgument={groupArgument}
                                            displayRegulatorArgument={displayRegulatorArgument}
                                            searchArgument={searchArgument}
                                            withApproval={withApproval}
                                            approved={approved}
                                            rejected={!!rejected}
                                            editable={isEditable}
                                            loading={isLoadingAfterAccessoryArgumentChange}
                                            onUpdate={onItemUpdate}
                                        />
                                    </_Collapse>
                                );
                            })}
                        </TransitionGroup>
                    </_List>
                </LocalizationProvider>
            )}
        </>
    );
};

QaArgumentList.displayName = 'QaArgumentList';

export default QaArgumentList;
